/**
 * Cookie Consent Banner
 *
 * GDPR-compliant consent banner displayed to users who have not yet
 * made a cookie consent decision. Blocks analytics tracking until
 * explicit opt-in, except where the `analytics-region-default` flag puts the
 * browser outside the opt-in jurisdictions: there the default is applied on the
 * /flags response (`regionConsentDefault.ts`), and this banner hides itself. It shows
 * again if that default is later withdrawn.
 *
 * @compliance GDPR Article 7 — Conditions for consent
 * @compliance ePrivacy Directive Article 5(3)
 */

import { Box, Button, HStack, Text, Link, VStack } from '@chakra-ui/react';
import { useState, useCallback, useEffect } from 'react';
import {
  hasConsentDecision,
  acceptEssentialOnly,
  isRegionDefaultConsent,
  onRegionDefaultChange,
} from '../../utils/cookieConsent';
import { revokeGa4Consent } from '../../utils/ga4';
import {
  flushPendingAnalyticsEvents,
  grantAnalyticsConsent,
} from '../../utils/analyticsConsentGrant';
import { HELP_LINKS } from '../../constants/helpLinks';

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(() => !hasConsentDecision());

  useEffect(
    () =>
      onRegionDefaultChange(() => {
        setIsVisible(!hasConsentDecision());
        // A region default is an upgrade like "Accept all", so the payloads
        // stashed while capture was off are replayed now, for the same reason.
        if (isRegionDefaultConsent()) flushPendingAnalyticsEvents();
      }),
    [],
  );

  const handleAcceptAll = useCallback(() => {
    grantAnalyticsConsent();
    setIsVisible(false);
  }, []);

  const handleEssentialOnly = useCallback(() => {
    acceptEssentialOnly();
    // Not always a first decision: a CONSENT_VERSION bump re-shows the banner
    // to people who had consented, and their GA4 cookies are still here.
    revokeGa4Consent();
    setIsVisible(false);
  }, []);

  if (!isVisible) return null;

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      zIndex="banner"
      bg="bg.surface"
      borderTopWidth="1px"
      borderColor="border.default"
      boxShadow="lg"
      px={{ base: 4, md: 8 }}
      py={4}
    >
      <VStack maxW="container.xl" mx="auto" spacing={3} align="stretch">
        <Text fontSize="sm" color="text.secondary">
          We use cookies to improve your experience. Essential cookies keep Bower working
          (authentication, preferences). Analytics cookies help us understand how Bower is used so
          we can make it better.{' '}
          <Link href={HELP_LINKS.COOKIES} isExternal>
            Learn more
          </Link>
        </Text>
        <HStack spacing={3} flexWrap="wrap">
          <Button size="sm" onClick={handleAcceptAll}>
            Accept all
          </Button>
          <Button size="sm" variant="outline" onClick={handleEssentialOnly}>
            Essential only
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
